// Канонические react-query keys. Source of truth:
//   docs/specs/sprint-3/stage-1-contracts.md §8.2 (Sprint 3 keys)
//   docs/specs/sprint-4/stage-1-contracts.md §8.5 + stage-4-frontend.md §2.2 (Sprint 4 upload keys)
//   docs/specs/sprint-5/stage-1-contracts.md §6.2 (Sprint 5 dashboard + biomarkers keys)
// Любой ad-hoc queryKey в компоненте/хуке = баг.

import type { UploadStatus, TimelinePeriod, BiomarkerListFilter } from './types';

export const queryKeys = {
  authMe: ['auth-me'] as const,
  profileMe: ['profile-me'] as const,
  survey: ['survey'] as const,
  uploads: ['uploads'] as const,
  // HEAL-76 — лента уведомлений. Счётчика тут нет: он живёт в profileMe (D-7).
  notifications: ['notifications'] as const,
  upload: (id: number) => ['upload', id] as const,
  uploadsByStatus: (status: UploadStatus) => ['uploads', { status }] as const,
  // HEAL-46a — состав пакета. Под тем же umbrella ['uploads'], чтобы
  // invalidateQueries({queryKey: ['uploads']}) после подтверждения файла
  // каскадно освежал и экран пакета.
  uploadBatch: (id: number) => ['uploads', 'batch', id] as const,

  // Sprint 5: все biomarker-* ключи под единым ['biomarkers'] umbrella (RF-S5-29).
  // invalidateQueries({queryKey: ['biomarkers']}) каскадно сбрасывает list/detail/extras.
  dashboard: ['dashboard'] as const,
  // Sprint 6-organs — органная навигация под ['dashboard'] umbrella: данные органов
  // выводятся из тех же точек, что и /dashboard (единый расчёт, backend §3), поэтому
  // invalidateQueries({queryKey: ['dashboard']}) каскадно сбрасывает список+детали органов.
  organs: ['dashboard', 'organs'] as const,
  organ: (code: string) => ['dashboard', 'organs', code] as const,
  biomarkers: ['biomarkers'] as const,
  biomarkersList: (filter: BiomarkerListFilter) =>
    ['biomarkers', 'list', { filter }] as const,
  biomarker: (code: string) => ['biomarkers', 'detail', code] as const,
  biomarkerDetail: (code: string, period: TimelinePeriod) =>
    ['biomarkers', 'detail', code, { period }] as const,
  biomarkersExtras: ['biomarkers', 'extras'] as const,
  // Mini-sprint 6-action-plan — под umbrella ['biomarkers'] (каскадный invalidate).
  biomarkerActionPlan: (code: string) => ['biomarkers', 'action-plan', code] as const,

  // Sprint 5A.1 — Wellness insights (S-S5A-1).
  // Invalidate из usePatchSurveyStep/usePutSurvey при wellness_cache_invalidated=true.
  wellnessInsights: ['wellness-insights'] as const,

  // Sprint 5A.2 — Biomarker insights (S-S5A2-1, R3 H2 — umbrella pattern из RF-S5-29).
  // Каскадный invalidate через umbrella ['biomarker-insights'] сбрасывает обе specific keys.
  // Invalidate триггерится из:
  //   - uploads.ts confirm/reopen onSuccess (после change biomarker_points lifecycle)
  //   - survey.ts usePatchSurveyStep/usePutSurvey onSuccess когда wellness_cache_invalidated=true
  //     (derived patient_context участвует в biomarker_set_hash — R4 H1)
  biomarkerInsights: ['biomarker-insights'] as const,
  biomarkerSummary: ['biomarker-insights', 'summary'] as const,
  biomarkerInsight: (code: string) => ['biomarker-insights', 'detail', code] as const,

  // Mini-sprint 6-visits — umbrella ['visits'] (mirror RF-S5-29 biomarkers pattern).
  // invalidateQueries({queryKey: ['visits']}) каскадно сбрасывает list+detail+search.
  visits: ['visits'] as const,
  visitsList: (params: { q?: string; page?: number }) =>
    ['visits', 'list', params] as const,
  visit: (id: number) => ['visits', 'detail', id] as const,
};
